/* Önizleme sayfası — sitenin kendi oluşturucusuyla (tools/src/pages.ts), düzenleme kipinde.

   Üretici aynı işlevi çağırıyor; içerik `GET /api/admin/design/content` ile
   üreticinin okuduğu modelin aynısı. Yayından farklar:

     · bloklar `data-b` / `data-f` işaretleriyle basılıyor (edit: true)
     · `<base>` sitenin kökeni — kareler ve ikonlar oradan
     · style.css, i18n.js ve main.js satır içi: panelin paketindeki sürüm,
       yani henüz yayımlanmamış stil değişiklikleri de görünüyor
     · tasarım CSS'i `<style id="fg-design">` içinde — renk ve ölçü
       değişikliğinde sayfa yeniden çizilmiyor, yalnızca bu etiket değişiyor
     · açılış animasyonu kapalı
     · düzenleyicinin kendi stilleri (`fg-editor`) */

import type { Design } from '@frawgraphy/shared';
import type { SiteContent } from '@frawgraphy/tools/content';
import { designCss, fontsHref, i18nScript } from '@frawgraphy/tools/design';
import { createSite, type PageKey } from '@frawgraphy/tools/pages';
import { BLOCK_TEMPLATES, I18N_JS, MAIN_JS, PAGES, PARTIALS, STYLE_CSS } from '../design/sources.ts';
import { EDITOR_CSS } from './style.ts';

/** Önizlemedeki göreli adreslerin çözüldüğü yer: kareler ve ikonlar. */
export const PREVIEW_BASE = 'https://frawgraphy.com/';

function inlineScript(code: string): string {
  return code ? `<script>${code.replace(/<\/script/gi, '<\\/script')}</script>` : '';
}

export type Preview = { body: string; css: string };

/**
 * Bir sayfanın önizlemesi. `body` CSS'siz HTML: iki çizim arasında yalnızca
 * CSS değiştiyse kabuk sayfayı yeniden yüklemiyor, `fg-design`'ı değiştiriyor.
 */
export function previewPage(doc: Design, content: SiteContent, key: PageKey, slug?: string): Preview {
  const site = createSite(content, doc, { pages: PAGES, partials: PARTIALS, blocks: BLOCK_TEMPLATES }, { lang: 'tr', mode: 'legacy', turnstile: null, edit: true });
  const html = site.page(key, slug);
  const full = { ...doc, home: site.doc.home, shared: site.doc.shared, pages: site.doc.pages };
  const fonts = fontsHref(full);
  const body = html
    .replace('<head>', `<head>\n<base href="${PREVIEW_BASE}">`)
    .replace('class="loader" data-loader', 'class="loader" data-skip')
    .replace(
      /<link rel="stylesheet" href="(?:\.\.\/|\/)?assets\/css\/style\.css[^"]*">/,
      () =>
        `<style>${STYLE_CSS}</style>\n<style id="fg-design"></style>\n<style id="fg-editor">${EDITOR_CSS}</style>` +
        (fonts ? `\n<link rel="stylesheet" href="${fonts.replace(/&/g, '&amp;')}">` : '')
    )
    .replace(/<script src="(?:\.\.\/|\/)?assets\/js\/i18n\.js[^"]*" defer><\/script>/, () => inlineScript(I18N_JS) + inlineScript(i18nScript(site.en)))
    .replace(/<script src="(?:\.\.\/|\/)?assets\/js\/main\.js[^"]*" defer><\/script>/, () => inlineScript(MAIN_JS));
  return { body, css: designCss(full) };
}

/** Önizlemenin tam HTML'i: gövde + tasarım CSS'i yerinde. */
export function previewHtml(p: Preview): string {
  return p.body.replace('<style id="fg-design"></style>', () => `<style id="fg-design">${p.css}</style>`);
}
